import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper, Alert, AlertTitle } from '@mui/material';
import { ErrorOutlineOutlined, Refresh, Home } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useErrorStore } from '../../stores/errorStore';
import { log } from '../../utils/errors/logger';
import { isDev } from '../../config/env';

interface Props {
  children: ReactNode;
  fallbackComponent?: React.ComponentType<{ error: Error; errorInfo: ErrorInfo; reset: () => void }>;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Global Error Boundary - catches all unhandled React errors at the app level
 * Integrates with Zustand error store for centralized error management
 */
class GlobalErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    log.error('Global Error Boundary caught error', {
      error: error.message ?? 'Unknown error',
      stack: error.stack ?? null,
      componentStack: errorInfo.componentStack ?? null
    });

    // Add error to Zustand store
    const errorStore = useErrorStore.getState();
    errorStore.addError({
      type: 'unknown',
      message: error.message,
      context: 'Global Error Boundary',
      retryable: true,
      details: {
        stack: error.stack,
        componentStack: errorInfo.componentStack
      }
    });

    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    
    // Clear errors from store
    const errorStore = useErrorStore.getState();
    errorStore.clearErrors();
    
    log.info('Global error boundary reset');
  };

  handleGoHome = () => {
    this.handleReset();
    // Navigate to home/login page
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallbackComponent) {
        const FallbackComponent = this.props.fallbackComponent;
        return (
          <FallbackComponent
            error={this.state.error!}
            errorInfo={this.state.errorInfo!}
            reset={this.handleReset}
          />
        );
      }

      // Default error UI
      return (
        <GlobalErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

// Default fallback component
interface GlobalErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
  onGoHome: () => void;
}

const GlobalErrorFallback: React.FC<GlobalErrorFallbackProps> = ({
  error,
  errorInfo,
  onReset,
  onGoHome,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        backgroundColor: 'background.default',
      }}
    >
      <Paper
        sx={{
          maxWidth: 600,
          padding: 4,
          textAlign: 'center',
        }}
      >
        <ErrorOutlineOutlined
          sx={{
            fontSize: 64,
            color: 'error.main',
            marginBottom: 2,
          }}
        />

        <Typography variant="h4" gutterBottom>
          {t('error.global.title', 'Something went wrong')}
        </Typography>

        <Typography variant="body1" color="textSecondary" gutterBottom>
          {t('error.global.message', 'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.')}
        </Typography>

        {isDev && error && (
          <Alert severity="error" sx={{ marginTop: 2, marginBottom: 2, textAlign: 'left' }}>
            <AlertTitle>Development Error Details</AlertTitle>
            <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
              {error.message}
              {'\n\n'}
              {error.stack}
              {errorInfo?.componentStack && (
                <>
                  {'\n\nComponent Stack:'}
                  {errorInfo.componentStack}
                </>
              )}
            </Typography>
          </Alert>
        )}

        <Box sx={{ marginTop: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={onReset}
          >
            {t('error.global.retry', 'Try Again')}
          </Button>

          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={onGoHome}
          >
            {t('error.global.home', 'Go Home')}
          </Button>
        </Box>

        <Typography variant="caption" color="textSecondary" sx={{ marginTop: 2, display: 'block' }}>
          {t('error.global.support', 'If this problem persists, please contact our support team.')}
        </Typography>
      </Paper>
    </Box>
  );
};

// Wrapper component to provide hooks context
export const GlobalErrorBoundary: React.FC<Props> = ({ children, fallbackComponent }) => {
  return (
    <GlobalErrorBoundaryClass fallbackComponent={fallbackComponent}>
      {children}
    </GlobalErrorBoundaryClass>
  );
};

// Hook for manually triggering error boundary
export const useGlobalErrorBoundary = () => {
  const [, setState] = React.useState();
  
  return React.useCallback((error: Error) => {
    log.error('Manual error boundary trigger', { error: error.message });
    setState(() => {
      throw error;
    });
  }, []);
};
